import { Link } from "react-router-dom";
import { Target, Eye, Award, Users, ArrowRight } from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";

const values = [
  {
    icon: Target,
    title: "Innovation",
    desc: "We stay at the forefront of technology, constantly exploring new solutions to deliver better results for our clients.",
  },
  {
    icon: Eye,
    title: "Transparency",
    desc: "Clear communication and honest partnerships form the foundation of every client relationship.",
  },
  {
    icon: Award,
    title: "Excellence",
    desc: "We hold ourselves to the highest standards, delivering quality solutions that exceed expectations.",
  },
  {
    icon: Users,
    title: "Client-First",
    desc: "Every decision we make is guided by the success and satisfaction of our clients.",
  },
];

const milestones = [
  { year: "2014", event: "Company Founded" },
  { year: "2016", event: "First Enterprise ERP Deployment" },
  { year: "2018", event: "Cloud Services Division Launched" },
  { year: "2020", event: "Cyber Security Practice Established" },
  { year: "2022", event: "Expanded to 50+ Enterprise Clients" },
  { year: "2024", event: "200+ Projects Successfully Delivered" },
];

export default function AboutPage() {
  usePageMeta("About Us", "Learn about Vaana BuildLogic Technologies - a leading IT services company in New Delhi specializing in enterprise technology solutions, ERP, and cloud infrastructure.");
  return (
    <div data-testid="about-page" className="pt-20">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-[#0A122A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-[#00E5FF] font-semibold mb-4">
                About Us
              </p>
              <h1 className="text-5xl sm:text-6xl tracking-tight leading-none font-['Outfit'] font-bold text-white mb-6">
                Powering Digital Transformation
              </h1>
              <p className="text-[#94A3B8] leading-relaxed mb-6">
                Vaana BuildLogic Technologies is a leading IT services company based in New Delhi, specializing in enterprise technology solutions. With over a decade of experience, we help businesses modernize their infrastructure, secure their operations, and harness the power of cloud computing.
              </p>
              <p className="text-[#94A3B8] leading-relaxed">
                Our team of certified professionals brings deep expertise across IT infrastructure, ERP systems, cyber security, and cloud platforms. We partner with businesses of all sizes to deliver solutions that drive measurable outcomes.
              </p>
            </div>
            <div>
              <img
                src="https://images.pexels.com/photos/7964413/pexels-photo-7964413.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Our team"
                className="rounded-md border border-white/8 w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div data-testid="mission-card" className="p-10 bg-[#0A122A] border border-white/8 rounded-md">
              <div className="w-12 h-12 flex items-center justify-center bg-[#111D3D] rounded-md mb-6">
                <Target size={24} className="text-[#00E5FF]" />
              </div>
              <h3 className="text-2xl font-['Outfit'] font-bold text-white mb-4">Our Mission</h3>
              <p className="text-[#94A3B8] leading-relaxed">
                To empower organizations with innovative technology solutions that enhance operational efficiency, strengthen security posture, and accelerate growth. We are committed to delivering enterprise-grade services with a personalized approach that makes advanced technology accessible to businesses of all sizes.
              </p>
            </div>
            <div data-testid="vision-card" className="p-10 bg-[#0A122A] border border-white/8 rounded-md">
              <div className="w-12 h-12 flex items-center justify-center bg-[#111D3D] rounded-md mb-6">
                <Eye size={24} className="text-[#00E5FF]" />
              </div>
              <h3 className="text-2xl font-['Outfit'] font-bold text-white mb-4">Our Vision</h3>
              <p className="text-[#94A3B8] leading-relaxed">
                To become India's most trusted technology partner for SMEs and enterprises alike, known for transforming businesses through secure, scalable, and intelligent IT infrastructure. We envision a future where every business has access to world-class technology solutions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 md:py-32 bg-[#0A122A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="mb-16">
            <p className="text-xs tracking-[0.2em] uppercase text-[#00E5FF] font-semibold mb-4">
              Our Foundation
            </p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-['Outfit'] font-bold text-white">
              Core Values
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, i) => (
              <div
                key={i}
                data-testid={`value-card-${i}`}
                className="p-8 bg-[#060B19] border border-white/8 rounded-md group hover:border-[#00E5FF]/30 transition-all duration-300"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-[#111D3D] rounded-md mb-5 group-hover:bg-[#00E5FF]/10 transition-colors duration-300">
                  <value.icon size={20} className="text-[#00E5FF]" />
                </div>
                <h4 className="text-lg font-['Outfit'] font-semibold text-white mb-2">{value.title}</h4>
                <p className="text-sm text-[#94A3B8] leading-relaxed">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="mb-16">
            <p className="text-xs tracking-[0.2em] uppercase text-[#00E5FF] font-semibold mb-4">
              Our Journey
            </p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-['Outfit'] font-bold text-white">
              Milestones
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {milestones.map((m, i) => (
              <div
                key={i}
                data-testid={`milestone-${i}`}
                className="flex items-start gap-4 p-6 bg-[#0A122A] border border-white/8 rounded-md hover:border-[#00E5FF]/30 transition-all duration-300"
              >
                <span className="text-2xl font-bold font-['Outfit'] text-[#00E5FF] flex-shrink-0">
                  {m.year}
                </span>
                <p className="text-sm text-[#94A3B8] pt-1">{m.event}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#0A122A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-3xl sm:text-4xl tracking-tight font-['Outfit'] font-bold text-white mb-4">
            Let's Build Something Great Together
          </h2>
          <p className="text-[#94A3B8] max-w-xl mx-auto mb-10">
            Partner with us to bring your digital vision to life with secure, scalable solutions.
          </p>
          <Link
            to="/contact"
            data-testid="about-cta-button"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#00E5FF] text-[#060B19] font-semibold text-sm rounded-sm hover:bg-[#33EEFF] transition-all duration-300 btn-glow"
          >
            Contact Us <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
